import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";

/**
 * Simple generic CRUD manager.
 * Props:
 *  - resource: API path e.g. "/admin/news"
 *  - title
 *  - columns: [{ key, label, render? }]
 *  - fields: [{ name, label, type, required?, placeholder?, options? (for select), rows? (textarea) }]
 *  - defaultValues
 *  - idField: default "id"
 *  - testId
 */
export default function CrudManager({
    resource,
    title,
    columns,
    fields,
    defaultValues = {},
    idField = "id",
    testId,
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // null | {} | item
    const [form, setForm] = useState(defaultValues);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(resource);
            setItems(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resource]);

    const openCreate = () => {
        setForm(defaultValues);
        setEditing({});
    };
    const openEdit = (item) => {
        const initial = { ...defaultValues };
        fields.forEach((f) => {
            initial[f.name] = item[f.name] ?? defaultValues[f.name] ?? "";
        });
        setForm(initial);
        setEditing(item);
    };
    const close = () => {
        setEditing(null);
        setError("");
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = { ...form };
            // Convert numeric/boolean fields
            fields.forEach((f) => {
                if (f.type === "number" && payload[f.name] !== "" && payload[f.name] != null) {
                    payload[f.name] = Number(payload[f.name]);
                }
                if (f.type === "checkbox") {
                    payload[f.name] = Boolean(payload[f.name]);
                }
            });
            if (editing && editing[idField]) {
                await api.put(`${resource}/${editing[idField]}`, payload);
            } else {
                await api.post(resource, payload);
            }
            await load();
            close();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (item) => {
        if (!window.confirm("¿Eliminar este elemento? Esta acción no se puede deshacer.")) return;
        try {
            await api.delete(`${resource}/${item[idField]}`);
            await load();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div data-testid={testId}>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#082050]/70">Administración</div>
                    <h1 className="mt-2 text-3xl font-semibold text-[#082050] tracking-tight">{title}</h1>
                </div>
                <button
                    onClick={openCreate}
                    data-testid={`${testId}-btn-new`}
                    className="bg-[#082050] text-white px-5 py-3 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#082050]/90"
                >
                    <Plus size={14} /> Nuevo
                </button>
            </div>

            {error && !editing && (
                <div className="mb-6 text-sm text-red-600 border border-red-500 bg-red-50 px-4 py-3">{error}</div>
            )}

            <div className="bg-white border border-[#E2E4E9] overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-widest text-[#082050]/60 border-b border-[#E2E4E9]">
                            {columns.map((c) => (
                                <th key={c.key} className="px-4 py-3 whitespace-nowrap">{c.label}</th>
                            ))}
                            <th className="px-4 py-3 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[#4A5568]">Cargando...</td></tr>
                        )}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[#4A5568]">Sin elementos</td></tr>
                        )}
                        {items.map((it, i) => (
                            <tr key={it[idField]} data-testid={`${testId}-row-${i}`} className="border-b border-[#E2E4E9] last:border-b-0">
                                {columns.map((c) => (
                                    <td key={c.key} className="px-4 py-3 align-top">
                                        {c.render ? c.render(it) : (typeof it[c.key] === "boolean" ? (it[c.key] ? "Sí" : "No") : String(it[c.key] ?? ""))}
                                    </td>
                                ))}
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => openEdit(it)}
                                        data-testid={`${testId}-btn-edit-${i}`}
                                        className="inline-flex items-center gap-1 text-xs text-[#082050] hover:opacity-70 mr-4"
                                    >
                                        <Pencil size={12} /> Editar
                                    </button>
                                    <button
                                        onClick={() => remove(it)}
                                        data-testid={`${testId}-btn-delete-${i}`}
                                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:opacity-70"
                                    >
                                        <Trash2 size={12} /> Eliminar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing != null && (
                <div className="fixed inset-0 z-50 bg-[#082050]/50 flex items-center justify-center p-4" onClick={close} data-testid={`${testId}-modal`}>
                    <form
                        onSubmit={save}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-[#082050] tracking-tight">
                                {editing[idField] ? "Editar" : "Nuevo"}
                            </h2>
                            <button type="button" onClick={close} className="text-[#4A5568] hover:text-[#082050]">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {fields.map((f) => (
                                <div key={f.name}>
                                    <label className="text-xs uppercase tracking-widest text-[#082050]/70">{f.label}</label>
                                    {f.type === "textarea" ? (
                                        <textarea
                                            required={f.required}
                                            rows={f.rows || 4}
                                            value={form[f.name] ?? ""}
                                            onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                            data-testid={`${testId}-input-${f.name}`}
                                            className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                                        />
                                    ) : f.type === "select" ? (
                                        <select
                                            required={f.required}
                                            value={form[f.name] ?? ""}
                                            onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                            data-testid={`${testId}-input-${f.name}`}
                                            className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#082050]"
                                        >
                                            <option value="">Seleccionar...</option>
                                            {f.options.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    ) : f.type === "checkbox" ? (
                                        <div className="mt-2 flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={!!form[f.name]}
                                                onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                                                data-testid={`${testId}-input-${f.name}`}
                                            />
                                            <span className="text-sm text-[#4A5568]">{f.placeholder || "Activo"}</span>
                                        </div>
                                    ) : (
                                        <input
                                            type={f.type || "text"}
                                            required={f.required}
                                            placeholder={f.placeholder}
                                            value={form[f.name] ?? ""}
                                            onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                            data-testid={`${testId}-input-${f.name}`}
                                            className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        {error && <div className="mt-4 text-sm text-red-600 border border-red-500 bg-red-50 px-4 py-3">{error}</div>}

                        <div className="mt-8 flex justify-end gap-3">
                            <button type="button" onClick={close} className="px-6 py-3 text-sm text-[#4A5568] hover:text-[#082050]">
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                data-testid={`${testId}-btn-save`}
                                className="bg-[#082050] text-white px-6 py-3 text-sm font-medium hover:bg-[#082050]/90 disabled:opacity-50"
                            >
                                {saving ? "Guardando..." : "Guardar"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
